/**
 * From a typed or dictated sentence to a sparse vector the model can score.
 * Words, word pairs and triples carry the meaning and the order ("no me gusta"
 * is not "me gusta"); character pieces inside each word carry typos, accents
 * and endings ("vendi", "bendi", "vendido"). Everything is hashed into a fixed
 * number of slots, so the model needs no vocabulary file.
 */

export const FEATURE_DIM = 32768;

export type SparseVec = { index: Int32Array; value: Float32Array };

const WEIGHT = { word: 1, pair: 1, triple: 0.6, chars: 0.5, length: 0.5 } as const;
const MIN_CHARS_WORD = 3;
const CHAR_SIZES = [3, 4, 5] as const;

/** The short forms people type on a phone, and what they stand for. */
const SHORT_FORMS = new Map<string, string>([
  ['q', 'que'],
  ['k', 'que'],
  ['xq', 'porque'],
  ['pq', 'porque'],
  ['porq', 'porque'],
  ['pa', 'para'],
  ['x', 'por'],
  ['d', 'de'],
  ['tb', 'tambien'],
  ['tmb', 'tambien'],
  ['bn', 'bien'],
  ['ps', 'pues'],
  ['pz', 'pues'],
  ['porfa', 'por favor'],
  ['porfis', 'por favor'],
  ['plis', 'por favor'],
  ['pls', 'por favor'],
  ['xfa', 'por favor'],
  ['ntp', 'no te preocupes'],
]);

const GOOD = new Set(['😀', '😃', '😄', '😁', '😊', '🙂', '😍', '🥰', '😘', '👍', '🙏', '🎉', '💪', '🔥', '✅', '👏', '❤', '😎', '🤩', '🥳']);
const BAD = new Set(['😡', '😠', '🤬', '😤', '😒', '👎', '😞', '😢', '😭', '😩', '😫', '🙄', '😑', '💔']);

function feeling(emoji: string): string {
  if (GOOD.has(emoji)) return 'emopos';
  if (BAD.has(emoji)) return 'emoneg';
  return 'emo';
}

/**
 * Casing, accents and punctuation go the way the rules flatten them; on top of that the
 * short forms are spelled out, stretched letters squeezed, numbers made one token and
 * emoji kept as a feeling.
 */
export function routerNormalize(text: string): string {
  const flat = text
    .normalize('NFC')
    .replace(/\p{Extended_Pictographic}/gu, (emoji) => ` ${feeling(emoji)} `)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\p{N}+/gu, '0')
    .replace(/(\p{L})\1{2,}/gu, '$1');
  const words = flat
    .split(' ')
    .filter(Boolean)
    .map((word) => SHORT_FORMS.get(word) ?? word)
    .join(' ')
    .split(' ')
    .filter(Boolean);
  return words.filter((word, at) => !(word.startsWith('emo') && word === words[at - 1])).join(' ');
}

/** FNV-1a over the UTF-16 units of the feature name. */
export function hashFeature(feature: string): number {
  let hash = 0x811c9dc5;
  for (let at = 0; at < feature.length; at += 1) {
    hash ^= feature.charCodeAt(at);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function lengthBucket(words: number): string {
  if (words <= 3) return String(words);
  if (words <= 5) return '4-5';
  if (words <= 8) return '6-8';
  if (words <= 14) return '9-14';
  return '15+';
}

function namedFeatures(words: readonly string[]): Map<string, number> {
  const found = new Map<string, number>();
  const add = (name: string, weight: number) => found.set(name, Math.max(found.get(name) ?? 0, weight));
  const padded = ['^', ...words, '$'];
  words.forEach((word) => {
    add(`w|${word}`, WEIGHT.word);
    const wrapped = `<${word}>`;
    if (word.length < MIN_CHARS_WORD) add(`c|${wrapped}`, WEIGHT.chars);
    else for (const size of CHAR_SIZES) for (let at = 0; at + size <= wrapped.length; at += 1) add(`c|${wrapped.slice(at, at + size)}`, WEIGHT.chars);
  });
  for (let at = 0; at + 1 < padded.length; at += 1) add(`b|${padded[at]}|${padded[at + 1]}`, WEIGHT.pair);
  for (let at = 0; at + 2 < padded.length; at += 1) add(`t|${padded[at]}|${padded[at + 1]}|${padded[at + 2]}`, WEIGHT.triple);
  add(`l|${lengthBucket(words.length)}`, WEIGHT.length);
  return found;
}

export function featurize(text: string, dim: number): SparseVec {
  const words = routerNormalize(text).split(' ').filter(Boolean);
  if (words.length === 0) return { index: new Int32Array(0), value: new Float32Array(0) };
  const slots = new Map<number, number>();
  for (const [name, weight] of namedFeatures(words)) {
    const hash = hashFeature(name);
    const slot = hash % dim;
    const sign = (hash >>> 16) & 1 ? 1 : -1;
    slots.set(slot, (slots.get(slot) ?? 0) + sign * weight);
  }
  const entries = [...slots].filter(([, value]) => value !== 0).toSorted((a, b) => a[0] - b[0]);
  const norm = Math.sqrt(entries.reduce((sum, [, value]) => sum + value * value, 0)) || 1;
  return {
    index: Int32Array.from(entries, ([slot]) => slot),
    value: Float32Array.from(entries, ([, value]) => value / norm),
  };
}

const PROBES = ['¿Cuánto vendí hoy?', 'no me gusta esa propuesta, xq q va', 'quiero mejorar mis finanzas 😅', 'cuanto bendi ayeeer 15%', 'ok'];

/**
 * A fingerprint of how sentences are read (normalization, short forms, features, weights, hashing): the shipped
 * model stores it, so a model trained with another reader is refused instead of silently misreading every question.
 */
export function featureKey(dim: number): number {
  let hash = 0x811c9dc5;
  for (const probe of PROBES) {
    const vector = featurize(probe, dim);
    vector.index.forEach((slot, at) => {
      hash = Math.imul(hash ^ slot, 0x01000193);
      hash = Math.imul(hash ^ Math.round((vector.value[at] ?? 0) * 10000), 0x01000193);
    });
  }
  return hash >>> 0;
}
